import { useConfig } from 'config/use-config';
import { EffectiveCartShippingMethod } from 'interfaces/cart';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

interface InputType {
  shippingMethods?: Array<EffectiveCartShippingMethod>;
}

interface OutputType {
  cheapestShippingMethod?: EffectiveCartShippingMethod;
  insufficientMoneyFormatted?: string;
  isShippingFree: boolean;
}

/**
 * Hooks to return cheapestShippingMethod, calcurated insufficient money in money format and value if shipping is free.
 */
const useFreeShippingThreshold = ({ shippingMethods = [] }: InputType): OutputType => {
  const { currencyCode } = useConfig();
  const intl = useIntl();
  const cheapestShippingMethod = useMemo(() => {
    const sortedShippingMethods = [...shippingMethods].sort((a, b) => a.price - b.price);
    if (!sortedShippingMethods[0]) return null;
    return sortedShippingMethods[0];
  }, [shippingMethods]);

  const insufficientMoneyFormatted = useMemo(() => {
    if (!cheapestShippingMethod) return null;

    return intl.formatNumber(cheapestShippingMethod.reducedRequired / 100, {
      style: 'currency',
      currency: currencyCode,
    });
  }, [cheapestShippingMethod, currencyCode, intl]);

  const isShippingFree = useMemo(() => {
    if (!cheapestShippingMethod) return false;

    return cheapestShippingMethod.price === 0;
  }, [cheapestShippingMethod]);

  return {
    cheapestShippingMethod: cheapestShippingMethod || undefined,
    insufficientMoneyFormatted: insufficientMoneyFormatted || undefined,
    isShippingFree,
  };
};

export default useFreeShippingThreshold;
